/**
 * IFRAP-AI.fr - Public Layout
 * Institutional design (République Numérique style)
 * 
 * Palette: Bleu #000091, Rouge #E1000F, Fond #F8F9FA
 * Typography: Playfair Display (headings) + Inter (body)
 */

import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CyberAgnesChat from '../components/CyberAgnesChat';

const PublicLayout = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/publications', label: 'Publications' },
    { path: '/veille', label: 'Veille' },
    { path: '/infographies', label: 'Infographies' },
    { path: '/comparaison', label: 'France vs Europe' },
    { path: '/about', label: 'À propos' },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#000091] rounded-sm flex items-center justify-center">
                <span className="text-white font-bold text-lg">IF</span>
              </div>
              <div>
                <span className="text-[#000091] font-semibold text-lg tracking-tight">IFRAP-AI</span>
                <span className="text-[#E1000F] text-xs block -mt-1">Fondation IFRAP</span>
              </div>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navLinks.map(({ path, label }) => (
                <Link
                  key={path}
                  to={path}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === path
                      ? 'text-[#000091] border-b-2 border-[#000091] pb-1'
                      : 'text-gray-600 hover:text-[#000091]'
                  }`}
                >
                  {label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <Link
                  to="/admin"
                  className="bg-[#000091] text-white px-4 py-2 text-sm font-medium rounded hover:bg-[#000091]/90 transition-colors"
                  data-testid="admin-link"
                >
                  Back-office
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="text-[#000091] hover:text-[#000091]/80 text-sm font-medium"
                  data-testid="login-link"
                >
                  Connexion
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#000091] text-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* About */}
            <div className="col-span-2">
              <h3 className="text-lg font-semibold mb-4">Fondation IFRAP</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Think tank dédié à l'analyse des politiques publiques françaises.
                Notre mission : éclairer le débat public par des études rigoureuses
                et des données vérifiées.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Navigation</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/" className="hover:text-white">Accueil</Link></li>
                <li><Link to="/etudes" className="hover:text-white">Études</Link></li>
                <li><Link to="/kpis" className="hover:text-white">Indicateurs</Link></li>
                <li><Link to="/about" className="hover:text-white">À propos</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Contact</h4>
              <address className="text-sm text-gray-300 not-italic">
                32-34 rue des Jeûneurs<br />
                75002 Paris<br />
                Tél : 01 42 33 30 39
              </address>
            </div>
          </div>

          <div className="border-t border-white/20 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Fondation IFRAP. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
      {/* CyberAgnès Chat Widget */}
      <CyberAgnesChat variant="public" />
    </div>

  );
};

export default PublicLayout;